import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { brand, fonts, streamPalettes } from '../../design/palettes';
import { analyticsConfigured, initAnalytics, revokeAnalytics } from '../../analytics/gtag';
import {
  COOKIE_SETTINGS_EVENT,
  getStoredConsent,
  storeConsent,
  type ConsentValue,
} from '../../analytics/consent';

// Opt-in cookie-consent banner for analytics. It only ever appears on builds
// that ship a Google Analytics property (analyticsConfigured); otherwise it
// renders nothing. Analytics is loaded ONLY after the user accepts — declining
// (the default state) loads nothing. The banner can be reopened anywhere via
// the COOKIE_SETTINGS_EVENT (the Privacy Policy's "Cookie settings" control),
// satisfying the "withdraw consent as easily as you gave it" requirement.

const c = streamPalettes.Lagoon;

export function CookieConsent() {
  // Surface the banner when analytics is available and no choice has been made.
  const [open, setOpen] = useState(() => analyticsConfigured && getStoredConsent() === null);

  // On first mount, honor a previously granted consent by starting analytics.
  useEffect(() => {
    if (analyticsConfigured && getStoredConsent() === 'granted') initAnalytics();
  }, []);

  // Allow the banner to be reopened from a "Cookie settings" link elsewhere.
  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener(COOKIE_SETTINGS_EVENT, handler);
    return () => window.removeEventListener(COOKIE_SETTINGS_EVENT, handler);
  }, []);

  if (!analyticsConfigured || !open) return null;

  const choose = (value: ConsentValue) => {
    storeConsent(value);
    setOpen(false);
    if (value === 'granted') initAnalytics();
    else revokeAnalytics();
  };

  return (
    <div role="dialog" aria-label="Cookie consent" aria-live="polite" style={wrapStyle}>
      <div style={cardStyle}>
        <div style={textStyle}>
          We use a few <strong style={{ color: brand.ink }}>essential cookies</strong> to keep you signed in. With your
          permission, we’d also like to use <strong style={{ color: brand.ink }}>Google Analytics</strong> cookies to
          understand, in aggregate, how Gooey is used so we can improve it. You can change your choice anytime.{' '}
          <Link to="/privacy" style={linkStyle}>
            Learn more in our Privacy Policy
          </Link>
          .
        </div>
        <div style={btnRowStyle}>
          <button type="button" onClick={() => choose('denied')} style={declineBtnStyle}>
            Decline analytics
          </button>
          <button type="button" onClick={() => choose('granted')} style={acceptBtnStyle}>
            Accept analytics
          </button>
        </div>
      </div>
    </div>
  );
}

const wrapStyle: React.CSSProperties = {
  position: 'fixed',
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 1000,
  display: 'flex',
  justifyContent: 'center',
  padding: 16,
  pointerEvents: 'none',
};
const cardStyle: React.CSSProperties = {
  pointerEvents: 'auto',
  width: '100%',
  maxWidth: 720,
  background: c.surface,
  border: `1px solid ${c.line}`,
  borderRadius: 16,
  boxShadow: '0 18px 48px rgba(12,17,22,0.16)',
  padding: '18px 20px',
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 16,
  fontFamily: fonts.sans,
};
const textStyle: React.CSSProperties = {
  flex: '1 1 320px',
  fontSize: 13.5,
  lineHeight: 1.55,
  color: c.mute,
};
const linkStyle: React.CSSProperties = {
  color: brand.lagoon,
  textDecoration: 'underline',
  textUnderlineOffset: 2,
};
const btnRowStyle: React.CSSProperties = {
  display: 'flex',
  gap: 10,
  flexShrink: 0,
};
const baseBtnStyle: React.CSSProperties = {
  borderRadius: 10,
  padding: '10px 16px',
  fontSize: 13.5,
  fontWeight: 600,
  cursor: 'pointer',
  fontFamily: 'inherit',
  whiteSpace: 'nowrap',
};
// Decline and Accept are given equal visual weight so consent is freely given.
const declineBtnStyle: React.CSSProperties = {
  ...baseBtnStyle,
  background: c.surface,
  color: c.ink,
  border: `1px solid ${c.line}`,
};
const acceptBtnStyle: React.CSSProperties = {
  ...baseBtnStyle,
  background: c.ink,
  color: c.surface,
  border: `1px solid ${c.ink}`,
};
